import angular from 'angular'
import { ApiUser, Role } from '@vend/types'
import { fetchApiJson, trackError, userHasPermission } from '@vend/utilities'
import { EventEmitter } from '../util/event-emitter'
import { WEB_REGISTER } from '../components/web-register.constant'
import {
  FEATURE_ENABLE_CONTROL_ON_DISCOUNT,
  FEATURE_ENABLE_REGISTER_ACCESS_CONTROL,
  InsufficientStateForOfflineError,
  MAX_DISCOUNT_PERCENT,
  MIN_DISCOUNT_PERCENT,
  PERMISSIONS,
  RULES,
} from '../constants'
import { usersStore } from '../factories/database/users-store'
import {
  posStateManager,
  webRegisterStatusManager,
} from '../ng-compat/managers'
import { getFeatureFromCache } from '../util/feature-cache'

// Collect unique values from an array of objects by key
const collectUnique = <T extends Record<string, any>, K extends keyof T>(
  items: T[],
  key: K
): Array<NonNullable<T[K]>> => {
  return Array.from(
    new Set(items.map(item => item[key]).filter(Boolean))
  ) as Array<NonNullable<T[K]>>
}

// Add 30% jitter to 2 minutes (120000ms)
const JITTER_PERCENTAGE = 0.3
const UPDATE_USER_INTERVAL = 120000 * (1 + Math.random() * JITTER_PERCENTAGE)

const ACCOUNT_TYPES = {
  CASHIER: 'cashier',
  MANAGER: 'manager',
  ADMIN: 'admin',
}

// The user account_type with their privilege level
const PRIVILEGES = {
  [ACCOUNT_TYPES.CASHIER]: 0,
  [ACCOUNT_TYPES.MANAGER]: 1,
  [ACCOUNT_TYPES.ADMIN]: 2,
}

export interface LoggedInUserChangeEvent {
  loggedInUser: ApiUser
}

type Events = {
  loggedInUserChange: [LoggedInUserChangeEvent]
}

// TODO: LR-10798 Update Role type from @vend/types with the new rule structure
type Rule = {
  name: string
  config: { [key: string]: string | number }
  max_discount?: number
}

type Permission = {
  name: string
}

export interface RolesConnection {
  roles: UserManagerRole[]
}

export type UserManagerRole = Role & {
  rules: Rule[]
  permissions: Permission[]
}

/**
 * Factory for managing users.
 *
 * @class UserManager
 * @constructor
 * @extends {EventEmitter}
 */
export class UserManager extends EventEmitter<Events> {
  private readonly signoutUrl: string
  private whenInitialised?: Promise<UserManager | null>

  constructor() {
    super()
    this.signoutUrl = '/signout?return=' + window.encodeURI(WEB_REGISTER.URL)
  }

  /**
   * Initialises the user manager, retrieving the logged in user.
   *
   * @method init
   *
   * @return {Promise<UserManager>} A promise resolved with the user manager instance once initialisation is
   *         complete
   */
  public async init() {
    if (!this.whenInitialised) {
      this.whenInitialised = (async () => {
        try {
          const user = await this.fetchUserWithFallback()
          this.setLoggedInUser(user)
          setInterval(() => this.updateLoggedInUser(), UPDATE_USER_INTERVAL)
          return this
        } catch (error) {
          trackError('[UserManager] Failed to initialise user manager', {
            data: { error },
          })
          return null
        }
      })()
    }
    return this.whenInitialised
  }

  /**
   * Fetch the logged in user from the API, falling back to the POS state manager if the API request fails.
   * This method is used to ensure the user is fetched from the API when the app is online.
   * If the app is offline, the user is fetched from the POS state manager.
   * If the user is not found in the POS state manager, an error is thrown.
   * Similar to https://github.com/vend/webregister/blob/c0df639be5dbf302a2042ae956013fbd534ee403/assets/scripts/configs/active-user-config.ts
   */
  private async fetchUserWithFallback(): Promise<ApiUser> {
    await webRegisterStatusManager.init()

    if (webRegisterStatusManager.isOnline()) {
      const user = await this.fetchUser()
      if (!user) {
        return this.getLoggedInUser()
      }

      return user
    }

    return this.getLoggedInUser()
  }

  /**
   * Get the logged in user's details.
   *
   * @method getLoggedInUser
   *
   * @return {User} The logged in user's details
   */
  public getLoggedInUser(): ApiUser {
    const user = posStateManager.getUser()
    if (user) {
      return user
    }
    throw new InsufficientStateForOfflineError('No logged in user found')
  }

  /**
   * Log out the current user - clearing the logged in user state from the POS state manager and redirecting to the
   * signout URL.
   *
   * @method logout
   */
  public logout() {
    this.clearUser()
    window.location.assign(this.signoutUrl)
  }

  /**
   * Asserts whether the active user has a certain permission.
   *
   * @method hasPermission
   *
   * @param {String} permissionName
   *        The name of the API permission to check
   *
   * @return {Boolean} True if a given user has permission
   */
  public hasPermission(permissionName: string) {
    // @todo: once we are confident about permissions being resolved appropriately for admins, remove `hasAdminAccess`
    return (
      this.hasAdminAccess() ||
      userHasPermission(this.getLoggedInUser(), permissionName)
    )
  }

  /**
   * Whether the current logged in user has admin access (i.e. is an admin).
   *
   * @method hasAdminAccess
   *
   * @return {Boolean} true if the logged in user has admin access, false otherwise
   */
  public hasAdminAccess() {
    return this.hasAccess(ACCOUNT_TYPES.ADMIN)
  }

  /**
   * Whether the given user can access the specified outlet
   *
   * @method canUserAccessOutlet
   *
   * @param {User} user
   *        The user to check if they have access to the outlet
   *
   * @param {String} outletId
   *        The id of the outlet to check if user can access
   *
   * @return {Boolean} true if the given user has access to the outlet id
   */
  public canUserAccessOutlet(user: ApiUser, outletId: string) {
    const canAccessAllOutlets =
      !user.restricted_outlet_ids || !user.restricted_outlet_ids.length
    if (canAccessAllOutlets) {
      return true
    }
    return user.restricted_outlet_ids!.includes(outletId)
  }

  /**
   * Hydrates each item with a full user object within a collection.
   *
   * @method hydrateUsers
   *
   * @param  {Array} items
   *         The collection of items with a `user_id` property.
   *
   * @return {Promise<Array>} A promise resolved once each item has been hydrated with the full user.
   */
  public hydrateUsers<T extends { user_id: string }>(
    items: T[]
  ): Promise<Array<T & { user?: ApiUser }>> {
    const userIds = collectUnique(items, 'user_id')

    const userPromises = userIds.map(userId => usersStore.get(userId))

    return Promise.all(userPromises).then(users => {
      const userMap = new Map<string, ApiUser>()
      for (const user of users) {
        if (user !== undefined) {
          userMap.set(user.id, user)
        }
      }

      for (const item of items) {
        ;(item as T & { user?: ApiUser }).user = userMap.get(item.user_id)
      }

      return items as Array<T & { user?: ApiUser }>
    })
  }

  /**
   * Set the cached logged in user to the given user.
   *
   * @method setLoggedInUser
   *
   * @param  {User} user
   *         The user to set as the logged in user
   */
  public setLoggedInUser(user: ApiUser) {
    if (!user) {
      return this.logout()
    }
    posStateManager.setUser(user)
    usersStore.put(user)
    this.emit('loggedInUserChange', {
      loggedInUser: user,
    })
  }

  /**
   * Get a rule from the logged in user
   *
   * @method getRule
   *
   * @param {RULES} ruleName
   *        The name of the rule to check
   *
   * @returns {Rule | null} rule if a given user has rule
   */
  public getRule(ruleName: RULES): Rule | null {
    // TODO: LR-10032 change vend.js to use latest User type from @vend/types
    const user = this.getLoggedInUser()

    if (user?.rules && user.rules.hasOwnProperty(ruleName)) {
      return user.rules[ruleName]
    }

    return null
  }

  /**
   * Asserts whether the active user has a certain rule
   *
   * @method hasRule
   *
   * @param {RULES} ruleName
   *        The name of the rule to check
   *
   * @return {Boolean} if a given user has rule
   */
  public hasRule(ruleName: RULES) {
    return Boolean(this.getRule(ruleName))
  }

  /**
   * Asserts whether the active user has line item discount rule enabled
   *
   * NOTE: This relies also on the `enable_control_on_discount` feature flag
   * and will check the presence of `sale.discount.manage` permission or
   * `sale.line_item_discount.manage` rule
   *
   * @method canApplyLineItemDiscount
   *
   * @return {Boolean} if a given user has line item discount rule or sale discount permission enabled
   */
  public canApplyLineItemDiscount() {
    return this.checkPermissionOrRule(
      PERMISSIONS.SALE_DISCOUNT,
      RULES.LINE_ITEM_DISCOUNT
    )
  }

  /**
   * Asserts whether the active user has sale discount rule enabled
   *
   * NOTE: This relies also on the `enable_control_on_discount` feature flag
   * and will check the presence of `sale.discount.manage` permission or
   * `sale.total_sale_discount.manage` rule
   *
   * @method canApplySaleDiscount
   *
   * @return {Boolean} if a given user has sale discount rule or sale discount permission enabled
   */
  public canApplySaleDiscount() {
    return this.checkPermissionOrRule(
      PERMISSIONS.SALE_DISCOUNT,
      RULES.TOTAL_SALE_DISCOUNT
    )
  }

  /**
   * Returns the maximum sale discount allowed in percentage
   * based on the rule for the active user
   *
   * NOTE: This relies also on the `enable_control_on_discount` feature flag
   * defaults to 0 if feature flag is enabled
   *
   * @method getMaxSaleDiscountPercent
   *
   * @return {Number} The sale discount rule for the given user
   */
  public getMaxSaleDiscountPercent() {
    const saleDiscountRule = this.getRule(RULES.TOTAL_SALE_DISCOUNT)

    if (this.isDiscountControlEnabled()) {
      const maxDiscount = saleDiscountRule?.max_discount
      return typeof maxDiscount === 'number'
        ? maxDiscount
        : MIN_DISCOUNT_PERCENT
    }

    return MAX_DISCOUNT_PERCENT
  }

  /**
   * Returns if a given role has sale discount rule or sale discount permission enabled
   * based on `enable_control_on_discount` feature flag value
   *
   * @method isRoleAllowedToApplyLineItemDiscount
   *
   * @return {Boolean} true if a given role has sale discount rule or sale discount permission enabled
   */
  public isRoleAllowedToApplySaleDiscount(role?: UserManagerRole) {
    if (!role) return false

    return this.isDiscountControlEnabled()
      ? role.rules.some(
          value =>
            value.name === RULES.TOTAL_SALE_DISCOUNT &&
            Object.keys(value.config).length > 0
        )
      : role.permissions.some(value => value.name === PERMISSIONS.SALE_DISCOUNT)
  }

  /**
   * Returns if the logged in user has access to the sell screen
   *
   * @method hasAccessToSellScreen
   *
   * @return {Boolean}
   */
  public hasAccessToSellScreen() {
    if (!getFeatureFromCache(FEATURE_ENABLE_REGISTER_ACCESS_CONTROL)) {
      return true
    }

    return (
      this.hasPermission(PERMISSIONS.ACCESS) &&
      this.hasPermission(PERMISSIONS.SALE_PROCESS)
    )
  }

  // ============================== Private API ============================== //
  /**
   * Check if discount control feature flag is enabled.
   *
   * @private
   * @method isDiscountControlEnabled
   *
   * @return {Boolean} true if discount control feature flag is enabled
   */
  private isDiscountControlEnabled(): boolean {
    return getFeatureFromCache(FEATURE_ENABLE_CONTROL_ON_DISCOUNT)
  }

  /**
   * Check permission or rule based on discount control feature flag.
   *
   * @private
   * @method checkPermissionOrRule
   *
   * @param {string} permission - The permission name to check
   * @param {RULES} rule - The rule name to check
   *
   * @return {Boolean} true if user has the permission (when FF is off) or rule (when FF is on)
   */
  private checkPermissionOrRule(permission: string, rule: RULES): boolean {
    return this.isDiscountControlEnabled()
      ? this.hasRule(rule)
      : this.hasPermission(permission)
  }

  /**
   * Clears the logged in user stored in the POS state manager.
   *
   * @private
   * @method clearUser
   *
   */
  private clearUser() {
    posStateManager.setUser(null)
  }

  private async fetchUser(): Promise<ApiUser | undefined> {
    try {
      const resp = await fetchApiJson('/api/2.0/user')
      return resp.data
    } catch (e) {
      // we want to signal that we should
      // try get this from cache
      return undefined
    }
  }

  /**
   * Update the current logged in user - fetching the logged in user from the API and updating the local state if
   * the user or the user's details have changed.
   *
   * @private
   * @method updateLoggedInUser
   *
   * @return {Promise} A promise resolved once the logged in user has been updated
   */
  private updateLoggedInUser() {
    return this.fetchUserWithFallback()
      .then(user => this.setLoggedInUser(user))
      .catch(() => this.logout())
  }

  /**
   * Determine whether the current logged in user has the given access level (or higher).
   *
   * @private
   * @method _hasAccess
   *
   * @param  {String} accountType
   *         Account type representing the access level being queried e.g. "admin", "manager" or "cashier"
   *
   * @return {Boolean} true if the logged in user has at least the access level of the given account type, false
   *         otherwise
   */
  private hasAccess(accountType: string) {
    const user = this.getLoggedInUser()
    return user && PRIVILEGES[user.account_type] >= PRIVILEGES[accountType]
  }
}

export const userManager = new UserManager()

angular.module('webregister.managers').factory('userManager', () => userManager)
